import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { createSubLocation, type SubLocation } from "@/lib/wartungen";
import { ApiError } from "@/lib/apiClient";
import { toPathSegments } from "./planModel";

export interface CreateSubLocationDialogProps {
  open: boolean;
  onClose: () => void;
  heroAddressId: number | null;
  subLocations: SubLocation[];
  onCreated: (created: { id: number; path: string[] }) => void;
}

export function CreateSubLocationDialog({
  open,
  onClose,
  heroAddressId,
  subLocations,
  onCreated,
}: CreateSubLocationDialogProps) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setParentId(null);
    setError(null);
  }, [open]);

  const parentOptions = useMemo(
    () =>
      subLocations
        .map((entry) => {
          const id = Number(entry.id);
          if (!id) return null;
          const label =
            entry.path_label ||
            (Array.isArray(entry.path_segments) ? entry.path_segments.join(" > ") : "") ||
            entry.name ||
            `Unterort ${id}`;
          return { id, label: String(label) };
        })
        .filter(Boolean) as Array<{ id: number; label: string }>,
    [subLocations],
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!heroAddressId) {
      setError("Hero-Adresse fehlt.");
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Bitte einen Unterortnamen eingeben.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createSubLocation(heroAddressId, {
        name: trimmed,
        parent_id: parentId,
      });
      const id = Number(created?.id);
      if (!Number.isFinite(id) || id <= 0) throw new Error("Unterort-ID fehlt in der Antwort.");
      onCreated({ id, path: toPathSegments(created) });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unterort konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={name.trim() !== ""}
      title="Unterort anlegen"
      description="Neuen Unterort erstellen und danach direkt auswählen."
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button type="submit" form="create-sub-location-form" disabled={!heroAddressId || saving || !name.trim()}>
            {saving ? "Anlegen …" : "Anlegen"}
          </Button>
        </>
      }
    >
      <form id="create-sub-location-form" className="space-y-4" onSubmit={handleSubmit}>
        <label className="block space-y-1">
          <span className="eh-label text-xs">Neuer Unterortname</span>
          <input
            className="eh-input w-full"
            value={name}
            maxLength={120}
            placeholder="z. B. Technikraum EG"
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </label>
        <label className="block space-y-1">
          <span className="eh-label text-xs">Übergeordneter Unterort (optional)</span>
          <select
            className="eh-input w-full"
            value={parentId ?? ""}
            onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Basisort</option>
            {parentOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        {error ? <p className="text-sm text-red">{error}</p> : null}
      </form>
    </Dialog>
  );
}
